<script lang="ts" setup>
import '../../../css/choice.css';
import { reactive, ref, computed, onBeforeMount } from 'vue';
import TextInput from "../../components/TextInput.vue";
import ActionFoldingPanel from "../../components/ActionFoldingPanel.vue";
import { ICol, RenderModeType } from '../../lib/ChoiceTableHelpers';
import ChoiceTable from "../../components/ChoiceTable.vue";
import { ChoiceApi, TitledRow, LoadingSection } from '../../LibMain';
import { ReasonCategoryOptions, IEnhancedAccessAuditReportSearch, IEnhancedAccessAuditReportEntry, IEnhancedAccessAuditReportDisplay } from '../../lib/Api/EnhancedAccess';
import ResizeMonitor from '../../components/ResizeMonitor.vue'
import { DateDisplayFormat } from
    '../../Enums/UiEnums'
import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
import * as DateUtils from '../../lib/DateUtils';
import ActionButtonV2 from '../../components/ActionButtonV2.vue';
import vSelect from 'vue-select';
import { ICompanies } from '../../lib/Api/Sentinel';
import { ActionButtonStyle } from '../../lib/Types/Types';
import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
import { ButtonDisplaySize, ButtonDisplayStyle } from '../../Enums/UiEnums';

const props = withDefaults(defineProps<{
    showDatabaseSearch: boolean
}>(), {
    showDatabaseSearch: false
});
onBeforeMount(async () => {
    if (props.showDatabaseSearch)
        compData.CompaniesList = await ChoiceApi.enhancedAccess.getOMDCompanies() ?? [];
});
const compData = reactive<{
    UserIDSearch: string,
    ReasonCategory: ReasonCategoryOptions[],
    TicketNumberSearch: string,
    ReasonBodySearch: string,
    DatabaseKey: string[],
    StartDate?: string,
    EndDate?: string,
    ReportData?: IEnhancedAccessAuditReportEntry[],
    ShowReport: boolean,
    SearchHeight: number,
    WindowHeight: number,
    ReasonOptions: ReasonCategoryOptions[],
    CompaniesList: ICompanies[]
}>({
    ReportData: undefined,
    ShowReport: false,
    StartDate: DateUtils.toDisplayDateString(DateUtils.toDate(DateUtils.addMonth(new Date(), -1)), DateDisplayFormat.NumericDate),
    EndDate: DateUtils.toDisplayDateString(DateUtils.toDate(DateUtils.endOfDay()), DateDisplayFormat.NumericDate),
    SearchHeight: 450,
    WindowHeight: (window.innerHeight - 450),
    ReasonOptions: Object.values(ReasonCategoryOptions),
    CompaniesList: [],
    ReasonCategory: [],
    DatabaseKey: [],
    TicketNumberSearch: "",
    ReasonBodySearch: "",
    UserIDSearch: ""
});

const FiltersInDefaultState = computed<boolean>(() => {
    if (compData.ReasonCategory.length > 0)
        return false;
    if (!(compData.ReasonBodySearch === ""))
        return false;
    if (!(compData.TicketNumberSearch === ""))
        return false;
    if (!(compData.UserIDSearch === ""))
        return false;
    if (compData.DatabaseKey.length > 0)
        return false;

    return true;
});
const showList = computed<IEnhancedAccessAuditReportDisplay[] | undefined>(() => {
    return compData.ReportData?.map((auditReport) => {
        return {
            ...auditReport,
            DatabaseName: compData.CompaniesList.find(o => o.database_key == auditReport.databaseKey)?.database_name ?? "NA"
        };
    });
});

const tableCols = computed<ICol[]>(() => {
    if (props.showDatabaseSearch) {
        return [{ Name: 'User ID', DataField: 'userID' },
        { Name: 'Reason Category', DataField: 'reasonCategory' },
        { Name: 'Ticket ID', DataField: 'ticketNumber' },
        { Name: 'Reason Explanation', DataField: 'reasonBody' },
        { Name: 'Database Name', DataField: 'DatabaseName' },
        { Name: 'Posted Date', DataField: 'postDate', RenderMode: RenderModeType.PrettyDate }
        ];
    }
    return [{ Name: 'User ID', DataField: 'userID' },
    { Name: 'Reason Category', DataField: 'reasonCategory' },
    { Name: 'Ticket ID', DataField: 'ticketNumber' },
    { Name: 'Reason Explanation', DataField: 'reasonBody' },
    { Name: 'Posted Date', DataField: 'postDate', RenderMode: RenderModeType.PrettyDate }
    ];
});

function ClearReason() {
    compData.ReasonCategory = [];
    compData.ReasonBodySearch = "";
    compData.TicketNumberSearch = "";
    compData.UserIDSearch = "";
    compData.DatabaseKey = [];
}
async function GenerateReport() {
    compData.ReportData = undefined;
    compData.ShowReport = true;
    const reportRequest: IEnhancedAccessAuditReportSearch = {
        UserIDSearch: compData.UserIDSearch,
        ReasonFilterCategories: compData.ReasonCategory,
        TicketNumberSearch: compData.TicketNumberSearch,
        ReasonBodySearch: compData.ReasonBodySearch,
        DatabaseKeyFilters: compData.DatabaseKey,
        StartDate: compData.StartDate,
        EndDate: compData.EndDate
    };
    const result = await ChoiceApi.enhancedAccess.generateReport(reportRequest);
    if (!result) {
        return;
    }
    compData.ReportData = result;
    windowResize();
}

function windowResize() {
    compData.WindowHeight = window.innerHeight - compData.SearchHeight;
}
function onUpdateFilter(isPanelOpen: boolean): void {
    if (!isPanelOpen) {
        compData.SearchHeight = 70;
    }
    else {
        compData.SearchHeight = 500;
    }
    windowResize();
}
</script>
<template>
    <ResizeMonitor @resize="windowResize" />
    <div>
        <div class="isalus-page-title-large pt-2 pb-2">Enhanced Access Report</div>
        <ActionFoldingPanel panelTitle="Filter Criteria" @update:actionPanel="onUpdateFilter">
            <template #body>
                <TitledRow title="Reason Category Search" :titleWidth="2" class="mb-2">
                    <v-select style="font-size: 0.85rem" v-model="compData.ReasonCategory"
                        :options="compData.ReasonOptions" :multiple="true" :searchable="false" :filterable="true"
                        :closeOnSelect="true" :select-on-tab="true" />

                </TitledRow>
                <div v-show="props.showDatabaseSearch">
                    <TitledRow title="Database Name" :titleWidth="2" class="mb-2">
                        <v-select style="font-size: 0.85rem" v-model="compData.DatabaseKey"
                            :options="compData.CompaniesList" :multiple="true" :searchable="true" :filterable="true"
                            :reduce="item => item.database_key" :closeOnSelect="true" :select-on-tab="true"
                            label="database_name" />
                    </TitledRow>
                </div>
                <TitledRow title="User ID" :titleWidth="2" class="mb-2">
                    <TextInput v-model:textValue="compData.UserIDSearch" placeholder="User ID" :maxLength="20" />
                </TitledRow>
                <TitledRow title="Ticket Number Search" :titleWidth="2" class="mb-2">
                    <TextInput v-model:textValue="compData.TicketNumberSearch" placeholder="Ticket Number"
                        :maxLength="50" />
                </TitledRow>
                <TitledRow title="Reason Body Search" :titleWidth="2" class="mb-2">
                    <TextInput class="mb-2" v-model:textValue="compData.ReasonBodySearch" :rows="2" :maxLength="1200" />
                </TitledRow>
                <TitledRow title="Start Date" :titleWidth="2" contentClass="col-sm-2">
                    <DatePickerNoTZ class="col" v-model:value="compData.StartDate" :clearable="false"
                        :internalDataFormat="DateDisplayFormat.NumericDate"
                        :displayFormat="DateDisplayFormat.PrettyDate" />
                </TitledRow>

                <TitledRow title="End Date" :titleWidth="2" class="mb-2" contentClass="col-sm-2">
                    <DatePickerNoTZ v-model:value="compData.EndDate" :clearable="false"
                        :internalDataFormat="DateDisplayFormat.NumericDate"
                        :displayFormat="DateDisplayFormat.PrettyDate" />
                </TitledRow>

                <ActionButtonV2 :action="ActionButtonStyle.Report" :text="'Generate'" :spinText="'Generating'"
                    :toolTip="'Generate Report of Enhanced Access Audit Logs'" :size="ButtonDisplaySize.Default"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" :performAction="GenerateReport"
                    :endingPadding="1" />

                <ChoiceButton v-show="!FiltersInDefaultState" :size="ButtonDisplaySize.Default"
                    :action="ActionButtonStyle.Delete" :displayStyle="ButtonDisplayStyle.OutlineDanger"
                    text=" Clear Filters" @click="ClearReason" />
            </template>
        </ActionFoldingPanel>
        <div v-if="compData.ShowReport">
            <LoadingSection :isLoading="!compData.ReportData" loadingMsg="Generating Report">
                <ChoiceTable :data="showList" :columns="tableCols" :height="compData.WindowHeight" :allowSorting="true"
                    :key-value-data-member="'auditID'" :showFilters="true" />
            </LoadingSection>
        </div>
    </div>
</template>